/**
 * @file        src/pages/hotel/AddMarkup.jsx
 * @description Create / edit a B2C hotel markup rule (margin_rules wrapper).
 *              Reused for /hotel/markups/new and /hotel/markups/:id/edit.
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-10-06 Claude — Domestic / International stays (`dom_int`), judged on
 *                          the property's country; part of the overlap check.
 *   - 2026-10-05 Claude — The amount is labelled PER ROOM / NIGHT: the booking
 *                          engine now charges it × rooms × nights (client
 *                          decision; booking_backend utils/hotelStay.js).
 *   - 2026-07-02 Claude — Initial (B2C Hotel Management).
 *   - 2026-07-12 Claude — Markup type is now 'fixed' (not 'flat'). margin_rules
 *                          .margin_type is ENUM('percentage','fixed'), so every
 *                          "flat" markup was truncated to '' by MySQL and saved as
 *                          a rule that priced nothing. Labels match the legacy
 *                          console ("Fix" / "Percent").
 *   - 2026-08-03 Claude — Required Brand field (registry code, no default) —
 *                          the API now demands `brand` on create.
 *   - 2026-08-10 Claude — margin_value must be > 0; price_max must exceed
 *                          price_min. The server also rejects a range that
 *                          overlaps another active rule (409) — surfaced via the
 *                          existing fields-error toast, so no new UI is needed.
 *   - 2026-09-12 Claude — Carry the Markup List's "only one rule applies" notice
 *                          onto the form itself, so the rule is stated where the
 *                          range is actually typed rather than one screen back.
 *   - 2026-09-15 Claude — Note now also states that the markup is already inside
 *                          the displayed price, so the convenience fee, discounts
 *                          and coupons are all computed on the marked-up total —
 *                          never on the supplier net.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import { MARKUP_BASE_NOTE } from '../../lib/pricingNotes';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import BrandSelect from '../../components/BrandSelect';
import { get, post, patch } from '../../api/client';
import { toast } from '../../lib/toast';
import { DOM_INT_SELECT_OPTIONS } from '../../lib/domInt';

// MUST match margin_rules.margin_type ENUM('percentage','fixed').
const schema = z.object({
  brand: z.string().trim().min(1, 'Required'),
  margin_type: z.enum(['percentage', 'fixed']),
  margin_value: z.coerce.number().min(0.01, 'Must be greater than 0'),
  // Required for B2C: a markup with no price band applies to nothing useful, so
  // both ends must be set. (onSubmit maps '' → undefined, which fails the number.)
  price_min: z.coerce.number({ invalid_type_error: 'Price Min is required' }).min(0, 'Must be 0 or more'),
  price_max: z.coerce.number({ invalid_type_error: 'Price Max is required' }).min(0, 'Must be 0 or more'),
  is_active: z.boolean(),
}).refine((d) => d.price_max > d.price_min, {
  message: 'Price Max must be greater than Price Min.',
  path: ['price_max'],
});

const empty = { brand: '', margin_type: 'fixed', margin_value: '', price_min: '', price_max: '', dom_int: '', is_active: true };

export default function AddMarkup() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data: existing } = useQuery({
    queryKey: ['hotel-markups', 'one', id],
    queryFn: () => get(`/hotel-markups/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const m = existing.data;
      setForm({
        brand: m.brand || '',
        margin_type: m.margin_type || 'fixed',
        margin_value: m.margin_value ?? '',
        price_min: m.price_min ?? '',
        price_max: m.price_max ?? '',
        dom_int: m.dom_int || '',
        is_active: !!m.is_active,
      });
    }
  }, [existing]);

  const mut = useMutation({
    mutationFn: (body) => (isEdit ? patch(`/hotel-markups/${id}`, body) : post('/hotel-markups', body)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Markup updated' : 'Markup added', 'Saved.');
      qc.invalidateQueries({ queryKey: ['hotel-markups'] });
      navigate('/hotel/markups');
    },
    onError: (err) => {
      const fields = err.response?.data?.error?.fields;
      if (fields) setErrors(fields);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const candidate = {
      ...form,
      price_min: form.price_min === '' ? undefined : form.price_min,
      price_max: form.price_max === '' ? undefined : form.price_max,
    };
    const parsed = schema.safeParse(candidate);
    if (!parsed.success) {
      const fieldErrs = {};
      for (const i of parsed.error.issues) fieldErrs[i.path.join('.')] = i.message;
      return setErrors(fieldErrs);
    }
    setErrors({});
    // Explicit null = both, so choosing All on edit clears a stored scope.
    mut.mutate({ ...parsed.data, dom_int: form.dom_int || null });
  }

  function onChange(key) {
    return (e) => setForm({ ...form, [key]: e.target.value });
  }

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Hotel Markup' : 'Add Hotel Markup'}
        subtitle="B2C Hotel Management"
        actions={<button className="btn ghost" onClick={() => navigate('/hotel/markups')}><Icon name="x" /> Cancel</button>}
      />

      <div className="notice-banner info">
        <span>
          Only <b>one</b> markup applies to a stay: the rule whose price range contains
          the stay&apos;s price; then a <b>Domestic- or International-only</b> rule beats
          one for both (a stay is domestic when the hotel is in India); the most
          recently updated wins a tie. A range that <b> overlaps</b> another active rule
          on the same brand and Domestic/International choice is rejected — edit the
          existing rule instead.
          {MARKUP_BASE_NOTE}
        </span>
      </div>

      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="Brand" required err={errors.brand}>
            <BrandSelect value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} invalid={!!errors.brand} />
          </Field>
          <Field label="Markup Type" required err={errors.margin_type}>
            <Select
              value={form.margin_type}
              onChange={(v) => setForm({ ...form, margin_type: v })}
              invalid={!!errors.margin_type}
              options={[{ value: 'fixed', label: 'Fix' }, { value: 'percentage', label: 'Percent' }]}
            />
          </Field>
          <Field label="Markup Value (per room / night)" required err={errors.margin_value}>
            <input type="number" min="0.01" step="0.01" value={form.margin_value} onChange={onChange('margin_value')} placeholder="e.g. 250 or 5" />
          </Field>
          <Field label="Price Min" required err={errors.price_min}>
            <input type="number" min="0" step="0.01" value={form.price_min} onChange={onChange('price_min')} placeholder="Applies from this booking amount" />
          </Field>
          <Field label="Price Max" required err={errors.price_max}>
            <input type="number" min="0" step="0.01" value={form.price_max} onChange={onChange('price_max')} placeholder="Applies up to this booking amount" />
          </Field>
          <Field label="Domestic / International">
            <Select
              value={form.dom_int}
              onChange={(v) => setForm({ ...form, dom_int: v })}
              options={DOM_INT_SELECT_OPTIONS}
            />
          </Field>

          <Field label="Status">
            <label className="check">
              <input type="checkbox" checked={form.is_active}
                     onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <span>Active</span>
            </label>
          </Field>

          <div className="form-actions">
            {!isEdit && (
              <button type="button" className="btn ghost" onClick={() => setForm(empty)}>Reset</button>
            )}
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Markup'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, required, err, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
